import { useCallback, useEffect, useRef, useState } from "react";
import { readSelectionKind, type SelectionSnapshot } from "../services/selectionKind";
import { hasOffice, hasPpt } from "../../../utils/formatUtils";
import { logEvent } from "../../../../../telemetry";
import type { ImageClassification } from "../services/classifyImageApi";
import type { ClassifiedSelection } from "../services/classifySelection";
import { devTrace } from "../../shared/services/devTrace";

const CHIPS_CONNECTOR = "deckorate";

/**
 * Classifies the selected picture — normally `classifySelectedImage` bound to an
 * HTTP client and client id.
 *
 * Injected rather than imported so this hook keeps no opinion about how the
 * request is made, and so the selection tracking can be tested without an HTTP
 * layer. Omitted (Word/Outlook, or a workspace without Deckorate configured)
 * means pictures simply keep the generic image chip set.
 *
 * Must resolve, never reject: `classifySelectedImage` already logs and falls
 * back, and a rejection here would be an unhandled one inside a debounced
 * selection read.
 */
export type ClassifyImage = () => Promise<ClassifiedSelection | null>;

/**
 * PowerPoint fires DocumentSelectionChanged for slide navigation as well as
 * shape selection, and clicking through a group of shapes can fire it several
 * times in a row. Coalescing the reads keeps us off the Office.js bridge during
 * a drag-select.
 */
const REREAD_DEBOUNCE_MS = 120;

/** Nothing selected — the initial state, and what a non-PowerPoint host keeps. */
const INITIAL: SelectionSnapshot = { kind: "slide", shapeCount: 0 };

/**
 * Reasons the dock can stop following the selection. Each leaves the tray frozen
 * on whatever it last showed, so every one of them needs a trace — a static tray
 * is indistinguishable from a correct one to the user, and unreported it is
 * indistinguishable from working code to us.
 */
const SELECTION_WARNING = {
  /** No `PowerPoint` global, in a host the caller had gated as PowerPoint. */
  HOST_API_UNAVAILABLE: "action-chip-host-api-unavailable",
  /** PowerPoint present but `Office.context.document` is not yet/no longer there. */
  OFFICE_CONTEXT_UNAVAILABLE: "action-chip-office-context-unavailable",
  HANDLER_ADD_FAILED: "action-chip-selection-handler-add",
  HANDLER_UNSUPPORTED: "action-chip-selection-handler-unsupported",
  HANDLER_REMOVE_FAILED: "action-chip-selection-handler-remove",
} as const;

type SelectionWarning = (typeof SELECTION_WARNING)[keyof typeof SELECTION_WARNING];

/**
 * Low-severity trace for anything that costs us the live selection. Warn rather
 * than error, and logged rather than thrown: the dock degrading to a static chip
 * set is not worth failing the chat panel over.
 */
function logSelectionWarning(reason: SelectionWarning): void {
  logEvent("connector.action.invoke", "warn", { connectorSystemName: CHIPS_CONNECTOR, reason });
}

function reportHandlerResult(reason: SelectionWarning) {
  return (result: Office.AsyncResult<void>): void => {
    if (result.status === Office.AsyncResultStatus.Failed) logSelectionWarning(reason);
  };
}

/**
 * Upgrade a picture snapshot with what the classifier found.
 *
 * An empty `bannerLabel` is dropped rather than written: the dock treats absent
 * as "use the local label", and an empty string would render a blank banner.
 */
function applyClassification(
  snapshot: SelectionSnapshot,
  classification: ImageClassification,
): SelectionSnapshot {
  return {
    ...snapshot,
    kind: classification.kind,
    ...(classification.bannerLabel ? { bannerLabel: classification.bannerLabel } : {}),
  };
}

/**
 * Tracks what the user has selected in PowerPoint so the chip dock can offer a
 * matching action set, re-reading on every DocumentSelectionChanged.
 *
 * This is the first reactive PowerPoint selection tracker in the app (the
 * existing helpers — `utils/validateSelection.ts` and colourOps'
 * `getSelectedShapesFlat`— are on-demand one-shot reads). The event wiring
 * mirrors SpeakerNotes' `useActiveSlideNotes`, which mirrors
 * `integrations/document/hooks/useWordDocument`.
 *
 * Safe to call unconditionally: without the PowerPoint APIs it subscribes to
 * nothing and stays on `slide`. It does record a warning in that case, because
 * its only caller is PowerPoint-gated — so a missing host API there is an
 * anomaly worth seeing, not the routine Word/Outlook path.
 */
export function usePptSelectionKind(classifyImage?: ClassifyImage): SelectionSnapshot {
  const [snapshot, setSnapshot] = useState<SelectionSnapshot>(INITIAL);
  // Guards against overlapping reads resolving out of order — a fast
  // click-through can start a second read before the first has synced.
  const requestIdRef = useRef(0);
  const debounceRef = useRef<number | null>(null);
  /**
   * Classifications already paid for, keyed by shape id. Kept for the lifetime
   * of the pane: clicking back and forth between two pictures must not re-upload
   * and re-classify each time, since every miss costs a blob PUT and a vision
   * call. A shape whose picture is replaced in place keeps its id and so keeps
   * the stale answer — the trade accepted when caching, and the reason this is
   * per-pane rather than persisted.
   */
  const classificationCacheRef = useRef(new Map<string, ImageClassification>());
  // Set on unmount. A read already awaiting the Office.js bridge cannot be
  // cancelled, so it is discarded on arrival instead — without this, a selection
  // change immediately before the pane closes writes state to an unmounted
  // component. The request-id guard alone doesn't cover it: the id still
  // matches, because unmounting doesn't start a newer read.
  const unmountedRef = useRef(false);

  const refresh = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    const next = await readSelectionKind();
    if (unmountedRef.current || requestId !== requestIdRef.current) return;

    // A picture whose classification is already paid for is upgraded before the
    // first paint, so re-selecting it never flashes the generic chips.
    const cached = next.kind === "image" && next.shapeId
      ? classificationCacheRef.current.get(next.shapeId)
      : undefined;
    const resolved = cached ? applyClassification(next, cached) : next;

    // The kind the host reported, before any classification. When a picture
    // reads as something other than `image` here, nothing downstream will ever
    // classify it — which is the first thing to check when the chips look wrong.
    devTrace("selection", "read", {
      hostKind: next.kind,
      shapeCount: next.shapeCount,
      shapeId: next.shapeId ?? "(none)",
      cacheHit: Boolean(cached),
      resolvedKind: resolved.kind,
    });

    // Same kind and count means the same chip set — skip the state write so an
    // unrelated slide-navigation event can't re-render the tray. The banner is
    // compared too: a cache hit can change the label while the kind is unchanged.
    setSnapshot((prev) =>
      prev.kind === resolved.kind &&
      prev.shapeCount === resolved.shapeCount &&
      prev.bannerLabel === resolved.bannerLabel
        ? prev
        : resolved,
    );

    // Only an unclassified picture is worth a vision call. `resolved.kind` is
    // checked rather than `next.kind` so a cache hit doesn't re-request.
    if (resolved.kind !== "image" || !classifyImage) {
      devTrace("selection", "no classification requested", {
        reason: resolved.kind !== "image" ? `kind is '${resolved.kind}', not an unclassified picture` : "no classifier bound",
      });
      return;
    }

    const classified = await classifyImage();
    // Discard a result that arrived after the user moved on: the request-id guard
    // covers a newer read, and the shape-id check covers the case where the id
    // is unavailable or the selection changed to a different picture.
    if (unmountedRef.current || requestId !== requestIdRef.current || !classified) return;
    if (next.shapeId && classified.shapeId !== next.shapeId) return;

    classificationCacheRef.current.set(classified.shapeId, classified.classification);
    setSnapshot((prev) =>
      // Re-checked against `prev` rather than written blindly: between the await
      // and here the tray may already show something else.
      prev.kind === "image" ? applyClassification(prev, classified.classification) : prev,
    );
  }, [classifyImage]);

  useEffect(() => {
    // ChatTabPanel only mounts the dock when the host is PowerPoint, so reaching
    // this guard means the host API is missing where it was expected — not the
    // ordinary Word/Outlook path. Selection tracking silently stops here, which
    // is the same end state as a failed handler registration below, so it gets
    // the same trace. The two halves are reported separately because they have
    // different causes: no PowerPoint global at all, versus PowerPoint present
    // but the document context absent (pane opened before Office.js was ready).
    if (!hasPpt()) {
      logSelectionWarning(SELECTION_WARNING.HOST_API_UNAVAILABLE);
      return;
    }
    if (!hasOffice()) {
      logSelectionWarning(SELECTION_WARNING.OFFICE_CONTEXT_UNAVAILABLE);
      return;
    }

    // Reset rather than rely on the initial value: a remount (React StrictMode's
    // double-invoke, or the pane being reopened) reuses the same ref instance,
    // and a stale `true` would silently stop the dock ever updating again.
    unmountedRef.current = false;

    const scheduleRefresh = (): void => {
      if (debounceRef.current !== null) globalThis.clearTimeout(debounceRef.current);
      debounceRef.current = globalThis.setTimeout(() => {
        debounceRef.current = null;
        if (unmountedRef.current) return;
        // readSelectionKind never rejects (it logs and falls back), so there is
        // no error path to handle here beyond satisfying the floating-promise rule.
        void refresh();
      }, REREAD_DEBOUNCE_MS);
    };

    void refresh();

    try {
      Office.context.document.addHandlerAsync(
        Office.EventType.DocumentSelectionChanged,
        scheduleRefresh,
        reportHandlerResult(SELECTION_WARNING.HANDLER_ADD_FAILED),
      );
    } catch {
      // Thrown synchronously when the host doesn't support the event. The dock
      // still works, it just stops following the selection.
      logSelectionWarning(SELECTION_WARNING.HANDLER_UNSUPPORTED);
    }

    return () => {
      unmountedRef.current = true;
      if (debounceRef.current !== null) {
        globalThis.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      try {
        Office.context.document.removeHandlerAsync(
          Office.EventType.DocumentSelectionChanged,
          { handler: scheduleRefresh } as Office.RemoveHandlerOptions,
          reportHandlerResult(SELECTION_WARNING.HANDLER_REMOVE_FAILED),
        );
      } catch {
        logSelectionWarning(SELECTION_WARNING.HANDLER_REMOVE_FAILED);
      }
    };
  }, [refresh]);

  return snapshot;
}
